// The fonts a brand may choose from (spec/03 §12 "Design tokens", decided in #463).
//
// **A curated list, never a URL.** A buyer picks a heading face and a body face by id from the list
// below; the platform serves the files itself. A font URL would be a third-party host in every
// surface's Content-Security-Policy and a request that tells that host who is sitting at which
// venue — a CSP hole and a privacy leak for the sake of a typeface.
//
// Every family here is under the SIL Open Font License 1.1, so the platform may host and subset it.
// The files are bundled with the web kit and the kiosk when their screens are built; until then a
// surface that does not have a face falls back through the stack, which is why each entry carries a
// real one. IBM Plex Mono, the numbers face (spec/BRAND.md §5), is not a choice: prices, timers and
// ids stay in it whatever the brand.
//
// Adding a family is adding a line here and its files to the kit. Ids are never reused or renamed:
// a stored brand names one.
/** The fallback every stack ends in — the design tokens' own (`packages/config/tokens`). */
export const BRAND_FONT_FALLBACK = "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
export const BRAND_FONTS = [
    // Clavian's own faces (spec/BRAND.md §5, Cobalt): Archivo for display, Inter for body.
    { id: 'archivo', family: 'Archivo', roles: ['display', 'body'] },
    { id: 'inter', family: 'Inter', roles: ['body'] },
    // The faces the Voltage tokens shipped with (retired by ADR-0023), kept so a stored choice stays valid.
    { id: 'big-shoulders-display', family: 'Big Shoulders Display', roles: ['display'] },
    { id: 'instrument-sans', family: 'Instrument Sans', roles: ['body'] },
    { id: 'hanken-grotesk', family: 'Hanken Grotesk', roles: ['body'] },
    // Condensed and technical display faces, in the spirit of esports lettering.
    { id: 'barlow-condensed', family: 'Barlow Condensed', roles: ['display'] },
    { id: 'oswald', family: 'Oswald', roles: ['display'] },
    { id: 'chakra-petch', family: 'Chakra Petch', roles: ['display'] },
    { id: 'exo-2', family: 'Exo 2', roles: ['display', 'body'] },
    // Latin and Devanagari in one family, for venues that speak Hindi (spec/03 §10).
    { id: 'rajdhani', family: 'Rajdhani', roles: ['display'] },
    { id: 'mukta', family: 'Mukta', roles: ['body'] },
    // Plain, very legible text faces.
    { id: 'ibm-plex-sans', family: 'IBM Plex Sans', roles: ['body'] },
    { id: 'source-sans-3', family: 'Source Sans 3', roles: ['body'] },
];
function idsWithRole(role) {
    const ids = BRAND_FONTS.filter((font) => font.roles.includes(role)).map((font) => font.id);
    return ids;
}
/** The families a brand may use for headings and display numbers. */
export const BRAND_DISPLAY_FONT_IDS = idsWithRole('display');
/** The families a brand may use for body text, interface and labels. */
export const BRAND_BODY_FONT_IDS = idsWithRole('body');
/** The font a stored id names, or `undefined` for an id that is not on the list. */
export function brandFont(id) {
    return BRAND_FONTS.find((font) => font.id === id);
}
/** The CSS `font-family` value for a font id: the family, then the fallback stack. */
export function brandFontStack(id) {
    const font = brandFont(id);
    if (font === undefined)
        throw new RangeError(`"${id}" is not a font on the brand list.`);
    return `'${font.family}', ${BRAND_FONT_FALLBACK}`;
}
//# sourceMappingURL=fonts.js.map